import { AlertTriangle, ChevronRight } from "@tamagui/lucide-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useState } from "react";
import { XStack, YStack, useMedia } from "tamagui";

import {
  getGetClinicalDataElementsQueryKey,
  useGetClinicalDataElements,
  useListUserPrograms,
} from "@/api/generated";
import {
  ConsentType,
  DeleteClinicalDataElementsRequest,
  ProgramID,
  ProgramStatus,
} from "@/api/generated/models";
import {
  useDeleteClinicalDataElements,
  useUpdateConsents,
} from "@/api/mutations/";
import { useGetClinicStatusForPatient } from "@/api/queries/clinicStatus";
import { invalidateConversationHistory } from "@/api/queries/conversation";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { LayoutContext, Section, Separator, ToggleSwitch } from "@/components";
import { Button } from "@/components/Button";
import { Header } from "@/components/Layout";
import { Skeleton } from "@/components/Skeletons";
import { H3, Paragraph, Text } from "@/components/Typography";
import {
  brandName,
  medicalGroupAbbr,
  medicalGroupName,
  shortBrandName,
} from "@/config/brand";
import { usePrivacySettingsExperience } from "@/hooks/privacySettings";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import DataPrivacyList from "@/settings/dataSharing/DataPrivacyList";
import DeleteDialog from "@/settings/dataSharing/DeleteDialog";
import RevokeConsentConfirmation from "@/settings/dataSharing/RevokeConsentConfirmation";

const ACCESS_PROGRAM_IDS = new Set<ProgramID>([
  ProgramID.access_eckm,
  ProgramID.access_ckm,
  ProgramID.access_behavioral_health,
]);

export default function DataSharingSettingsScreen() {
  const { showPrivacySettingsExperience } = usePrivacySettingsExperience();
  const router = usePropagatedRouter();
  const { lg } = useMedia();
  const queryClient = useQueryClient();
  const [deletePayload, setDeletePayload] =
    useState<DeleteClinicalDataElementsRequest | null>(null);
  const { mutate: updateConsents, isPending: isUpdating } = useUpdateConsents();
  const { mutate: deleteClinicalDataElements, isPending: isDeleting } =
    useDeleteClinicalDataElements();
  const { data: userProfile, isLoading: userProfileLoading } =
    useGetUserProfile();
  const { data: userPrograms, isLoading: userProgramsLoading } =
    useListUserPrograms();
  const { data: clinicStatus, isLoading: clinicStatusLoading } =
    useGetClinicStatusForPatient();
  const { setModalContent } = useContext(LayoutContext);
  const { data: clinicalDataElements, isLoading: clinicalDataElementsLoading } =
    useGetClinicalDataElements({
      query: {
        select: (data) => {
          const { visit_conversations, outreach_conversations } = data;
          const combined = [
            ...visit_conversations.map((item) => ({
              ...item,
              type: "visit" as const,
            })),
            ...outreach_conversations.map((item) => ({
              ...item,
              type: "outreach" as const,
            })),
          ];
          return combined.sort((a, b) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return dateB - dateA;
          });
        },
      },
    });

  const userEmail = userProfile?.email;
  const dataSharingConsentValue =
    userProfile?.consents[ConsentType.data_sharing];
  const dataSharingConsentEnabled = Boolean(dataSharingConsentValue);
  const hasNeverConsented = dataSharingConsentValue == null;
  const hieConsentEnabled = Boolean(
    userProfile?.consents[ConsentType.hie_consent],
  );
  const isAccessEnrolled = userPrograms?.some(
    (program) =>
      ACCESS_PROGRAM_IDS.has(program.program) &&
      program.status === ProgramStatus.enrolled,
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const handleConfirmRevoke = useCallback(() => {
    updateConsents({
      data: {
        consents: {
          [ConsentType.data_sharing]: false,
        },
        source: "settings-toggle",
      },
    });
    setShowConfirmation(false);
  }, [updateConsents]);

  const handleShowDeleteDialog = useCallback(() => {
    setShowDeleteDialog((prev) => {
      return !prev;
    });
  }, []);

  const handleDataConsentToggle = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        updateConsents({
          data: {
            consents: {
              [ConsentType.data_sharing]: true,
            },
            source: "settings-toggle",
          },
        });
      } else {
        setShowConfirmation(true);
      }
    },
    [updateConsents],
  );

  const handleHieConsentToggle = useCallback(
    (enabled: boolean) => {
      updateConsents({
        data: {
          consents: { [ConsentType.hie_consent]: enabled },
          source: "settings-toggle",
        },
      });
    },
    [updateConsents],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!deletePayload) return;
    deleteClinicalDataElements(
      { data: deletePayload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetClinicalDataElementsQueryKey(),
          });
          invalidateConversationHistory(queryClient);
          router.navigate("/");
          setModalContent(null);
        },
        onSettled: () => {
          setDeletePayload(null);
          setShowDeleteDialog(false);
        },
      },
    );
  }, [
    deleteClinicalDataElements,
    queryClient,
    deletePayload,
    router.navigate,
    setModalContent,
  ]);

  const isLoading =
    userProfileLoading || clinicStatusLoading || clinicalDataElementsLoading;

  const isSwitchDisabled =
    isUpdating ||
    (clinicStatus?.hasOngoingClinicVisit && dataSharingConsentEnabled);

  const hasClinicalDataElements =
    !isLoading && clinicalDataElements && clinicalDataElements.length > 0;

  return (
    <Section margin="$screenPadding" flex={1} $lg={{ flex: 1, gap: "$200" }}>
      {lg && (
        <Header gap="$250" marginBottom="$50">
          <H3>Data Sharing</H3>
          <Separator borderColor="$fillNeutral" />
        </Header>
      )}
      {isLoading && <DataPrivacyListSkeleton />}
      {!isLoading && !hasNeverConsented && !showConfirmation && (
        <>
          <YStack gap="$200">
            <ToggleSwitch
              label={`Data sharing between ${brandName} and ${medicalGroupName} (${medicalGroupAbbr})`}
              checked={dataSharingConsentEnabled}
              onCheckedChange={handleDataConsentToggle}
              disabled={isSwitchDisabled}
            />
            {clinicStatus?.hasOngoingClinicVisit && (
              <XStack gap="$100" alignItems="center">
                <AlertTriangle color="$iconBase" size="$225" />
                <Paragraph size="$sm">
                  You'll need to end your active clinic visit in order to
                  disable this.
                </Paragraph>
              </XStack>
            )}
          </YStack>
          <Separator borderColor="$fillNeutral" />
        </>
      )}

      {!isLoading && !hasNeverConsented && !showConfirmation && (
        <YStack gap="$100">
          <ToggleSwitch
            label="Health Information Exchange (HIE)"
            description="Share my health records through the HIE network to support my care."
            checked={hieConsentEnabled}
            onCheckedChange={handleHieConsentToggle}
            disabled={
              isUpdating ||
              userProgramsLoading ||
              (isAccessEnrolled && hieConsentEnabled)
            }
          />
          {isAccessEnrolled && hieConsentEnabled && (
            <XStack gap="$100" alignItems="center">
              <AlertTriangle color="$iconBase" size="$225" />
              <Paragraph size="$sm">
                HIE sharing is required while you're enrolled in an ACCESS
                program.
              </Paragraph>
            </XStack>
          )}
        </YStack>
      )}

      <RevokeConsentConfirmation
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmRevoke}
        userEmail={userEmail}
      />

      {hasClinicalDataElements && !showConfirmation && (
        <>
          <Paragraph italic size="$xs" color="$textSecondary">
            {shortBrandName} uses these clinical records to personalize your
            care — delete them if you'd prefer they stay private.
          </Paragraph>

          <DataPrivacyList
            clinicalDataElements={clinicalDataElements}
            handleSelect={(payload: DeleteClinicalDataElementsRequest) => {
              setDeletePayload(payload);
              handleShowDeleteDialog();
            }}
          />
          <Separator borderColor="$fillNeutral" />
        </>
      )}
      {!showConfirmation && (
        <Button.Wrapper
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          onPress={() => {
            showPrivacySettingsExperience();
          }}
        >
          <Text size="$sm">Access your privacy settings</Text>
          <ChevronRight color="$iconSecondary" flexShrink={0} />
        </Button.Wrapper>
      )}

      <DeleteDialog
        showDeleteDialog={showDeleteDialog}
        handleDialogOpenChange={handleShowDeleteDialog}
        handleConfirmDelete={handleConfirmDelete}
        handleClearDeletePayload={() => setDeletePayload(null)}
        isDeleting={isDeleting}
      />
    </Section>
  );
}

function DataPrivacyListSkeleton() {
  return (
    <YStack gap="$200">
      <Skeleton height="$400" />
      <Skeleton.Text lines={2} widths={["100%", "10%"]} height="$50" />
      <YStack gap="$150">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton height="$650" key={i} />
        ))}
      </YStack>
      <Skeleton.Text lines={2} widths={["100%", "10%"]} height="$50" />
    </YStack>
  );
}
